import type { RollGroupTracking } from '../types/roll';
import { rollNegatives, stepLabel } from '../utils/orderTracking';
import { shortDate } from '../utils/rollDetails';
import styles from './RollProgress.module.css';

/**
 * A roll's steps through the lab, for the customer: done ones with a tick and
 * their date, the next one marked as the current step, and what happens (or
 * happened) to the negatives. State is in the shape and the words, never the
 * colour alone.
 */
export function RollProgress({ rollName, tracking }: { rollName: string; tracking: RollGroupTracking }) {
  const negatives = rollNegatives(tracking.negatives);
  const parcelUrl = tracking.negatives.state === 'posted' ? tracking.negatives.parcel.url : undefined;
  return (
    <div className={styles.progress}>
      <ol className={styles.steps} aria-label={`Progress for ${rollName}`}>
        {tracking.steps.map((step) => (
          <li
            key={step.key}
            className={`${styles.step} ${styles[step.state]}`}
            aria-current={step.state === 'current' ? 'step' : undefined}
          >
            <span className={styles.marker} aria-hidden="true">
              {step.state === 'done' && (
                <svg viewBox="0 0 16 16" width="10" height="10">
                  <polyline points="3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="2.5" />
                </svg>
              )}
            </span>
            <span className={styles.label}>{stepLabel(step.key, tracking.negatives)}</span>
            {step.at && (
              <time className={styles.date} dateTime={step.at}>
                {shortDate(step.at)}
              </time>
            )}
            {step.state !== 'current' && (
              <span className={styles.srOnly}>{step.state === 'done' ? ', done' : ', to come'}</span>
            )}
          </li>
        ))}
      </ol>
      {negatives && (
        <p className={styles.negatives}>
          {negatives}
          {parcelUrl && (
            <>
              {' '}
              <a href={parcelUrl} target="_blank" rel="noopener noreferrer">
                Track your parcel<span className={styles.srOnly}> (opens in a new tab)</span>
              </a>
            </>
          )}
        </p>
      )}
    </div>
  );
}
